/**
 * Custom Sort - "Mais recentes" (New Products First)
 * Optimized for speed:
 * 1. Fetches lightweight JSON metadata for ALL products in parallel (fast).
 * 2. Sorts in memory.
 * 3. Fetches HTML only for the CURRENT page's products (fast).
 */

class CustomCollectionSort {
  constructor() {
    this.productsPerPage = 50; // Will be auto-detected
    this.currentPage = 1;
    this.allProductsMetadata = []; // { id, handle, title, is_new }
    this.sortedHandles = [];
    this.isLoading = false;
    this.collectionUrl = "";
    this.gridContainer = null;
    this.featuredBlocks = [];
    this.basePath = "";
    this.baseSearchParams = null;
    this.sortToggleObservers = new Map();

    // Bind methods
    this.handleSortChange = this.handleSortChange.bind(this);

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    // Detect collection URL and base params
    const collectionSection = document.querySelector(
      '[data-section-type="collection"]'
    );
    if (collectionSection) {
      this.collectionUrl =
        collectionSection.dataset.collection || window.location.pathname;
    } else {
      this.collectionUrl = window.location.pathname;
    }

    this.basePath = window.location.pathname;
    this.baseSearchParams = new URLSearchParams(window.location.search);
    this.currentPage = parseInt(this.baseSearchParams.get("page")) || 1;

    // Listen for sort changes
    this.initSortListener();

    // Check if custom sort is active
    if (this.baseSearchParams.get("sort_by") === "new-products") {
      this.startCustomSortFlow();
    }
  }

  initSortListener() {
    // Listen to the hidden input change that the theme's custom dropdown updates
    // Use capture phase to intercept before theme
    document.addEventListener(
      "change",
      (e) => {
        if (e.target && e.target.name === "sort_by") {
          if (e.target.value === "new-products") {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.handleSortChange(e.target.value);
          } else {
            setTimeout(() => this.teardownSortUI(), 0);
          }
        }
      },
      true
    );

    // Also listen to clicks on sort links if the theme uses <a> tags for sorting
    document.addEventListener(
      "click",
      (e) => {
        // Listen specifically for the custom option in the dropdown
        const link = e.target.closest(
          'a[data-value="new-products"], li[data-value="new-products"]'
        );
        if (link) {
          // Prevent theme's default AJAX behavior
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          // Force transition to custom sort URL
          this.handleSortChange("new-products");
        }
      },
      true
    ); // Use capture phase to catch it before theme does

    // Handle browser back/forward buttons
    window.addEventListener("popstate", () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("sort_by") === "new-products") {
        this.currentPage = parseInt(urlParams.get("page")) || 1;
        this.startCustomSortFlow();
        this.updateSortUI();
      } else {
        // If we moved away from new-products via history, ideally we should reload
        // to let the theme handle the other sort, or trigger theme's ajax.
        // Reload is safest.
        window.location.reload();
      }
    });
  }

  handleSortChange(value) {
    if (value === "new-products") {
      const currentUrl = new URL(window.location);

      // Update URL without reload
      currentUrl.searchParams.set("sort_by", "new-products");
      currentUrl.searchParams.set("page", "1");
      window.history.pushState({}, "", currentUrl);

      // Update UI manually since we blocked the theme's default action
      this.updateSortUI();

      // Start our custom flow immediately
      this.startCustomSortFlow();
    }
  }

  teardownSortUI() {
    this.sortToggleObservers.forEach((observer) => observer.disconnect());
    this.sortToggleObservers.clear();

    document
      .querySelectorAll(".popout--sort .popout__toggle")
      .forEach((toggle) => {
        if (toggle.dataset.customSortObserver) {
          delete toggle.dataset.customSortObserver;
        }
      });

    document
      .querySelectorAll(".popout--sort .popout__toggle__text")
      .forEach((span) => {
        span.style.removeProperty("display");
      });

    document.querySelectorAll(".custom-sort-active").forEach((el) => {
      el.classList.remove("custom-sort-active");
    });
  }

  updateSortUI() {
    const activeSort = new URLSearchParams(window.location.search).get(
      "sort_by"
    );
    if (activeSort !== "new-products") {
      this.teardownSortUI();
      return;
    }

    const applyChanges = () => {
      // 1. Find all relevant popout containers (Mobile + Desktop)
      const newProductLinks = document.querySelectorAll(
        'a[data-value="new-products"]'
      );
      const containers = new Set();

      newProductLinks.forEach((link) => {
        const container =
          link.closest("popout-select") || link.closest(".popout");
        if (container) containers.add(container);
      });

      containers.forEach((container) => {
        // A. Mark container as active
        container.classList.add("is-active", "custom-sort-active");
        container.classList.remove("is-expanded", "is-visible");

        // B. Handle the Toggle Button
        const toggle = container.querySelector(".popout__toggle");
        if (toggle) {
          toggle.setAttribute("aria-expanded", "false");
          toggle.classList.remove("is-active", "is-expanded", "is-visible");

          // C. Update Text Spans
          const spans = toggle.querySelectorAll(".popout__toggle__text");
          const spansArray = Array.from(spans);

          spansArray.forEach((span, index) => {
            const isValue =
              span.hasAttribute("data-sort-button-text") ||
              span.hasAttribute("data-popout-toggle-text") ||
              index === 1;

            if (isValue) {
              if (span.textContent !== "Mais recentes") {
                span.textContent = "Mais recentes";
              }
              span.style.display = "inline-block";
            } else {
              // Label ("Ordenar por") - Hide it
              span.style.display = "none";
            }
          });
        }

        // D. Close the List
        const list = container.querySelector(".popout-list");
        if (list) {
          list.classList.remove(
            "is-active",
            "is-expanded",
            "is-visible",
            "popout-list--visible"
          );
          list.setAttribute("aria-hidden", "true");
        }
      });

      // 2. Global cleanup
      document
        .querySelectorAll(".popout-list__item")
        .forEach((li) => li.classList.remove("is-active"));
      document
        .querySelectorAll(".popout-list__option")
        .forEach((a) => a.removeAttribute("aria-current"));

      newProductLinks.forEach((link) => {
        link.setAttribute("aria-current", "true");
        const li = link.closest(".popout-list__item");
        if (li) li.classList.add("is-active");
      });
    };

    // Apply immediately
    applyChanges();

    // Attach Observer to persist changes against theme JS
    const toggles = document.querySelectorAll(".popout--sort .popout__toggle");
    toggles.forEach((toggle) => {
      if (toggle.dataset.customSortObserver) return;

      const observer = new MutationObserver((mutations) => {
        if (
          new URLSearchParams(window.location.search).get("sort_by") !==
          "new-products"
        ) {
          return;
        }
        const valueSpan =
          toggle.querySelector("[data-sort-button-text]") ||
          toggle.querySelector(".popout__toggle__text:nth-child(2)");
        if (valueSpan && valueSpan.textContent !== "Mais recentes") {
          observer.disconnect();
          applyChanges();
          observer.observe(toggle, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }
      });

      observer.observe(toggle, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      toggle.dataset.customSortObserver = "true";
      this.sortToggleObservers.set(toggle, observer);
    });
  }

  startCustomSortFlow() {
    this.gridContainer = document.getElementById("CollectionLoop");
    if (!this.gridContainer) return;

    // Ensure UI is synced (in case of reload)
    this.updateSortUI();

    // Scroll to top of grid to mimic native behavior
    const gridOuter =
      document.querySelector(".grid-outer") || this.gridContainer;
    const headerHeight =
      document.querySelector(".site-header")?.offsetHeight || 0;
    const offset =
      gridOuter.getBoundingClientRect().top +
      window.scrollY -
      headerHeight -
      20;
    window.scrollTo({ top: offset, behavior: "smooth" });

    // Refresh params from current URL
    this.baseSearchParams = new URLSearchParams(window.location.search);

    // Detect products per page
    const productElements = this.gridContainer.querySelectorAll(
      "[data-product-handle]"
    );
    if (productElements.length > 0) {
      this.productsPerPage = productElements.length;
    }

    // Preserve non-product blocks
    this.captureFeaturedBlocks();

    this.showLoading();
    this.loadAndSort();
  }

  captureFeaturedBlocks() {
    if (!this.gridContainer) return;
    this.featuredBlocks = [];
    const children = Array.from(this.gridContainer.children);
    children.forEach((child, index) => {
      const isProduct =
        child.hasAttribute("data-product-handle") ||
        child.querySelector("[data-product-handle]");
      if (
        !isProduct &&
        !child.classList.contains("collection-loading") &&
        !child.classList.contains("no-results")
      ) {
        this.featuredBlocks.push({ index, html: child.outerHTML });
      }
    });
  }

  showLoading() {
    // Use theme's native loading class logic: .grid-outer.is-loading .grid__loader
    const gridOuter = document.querySelector(".grid-outer");
    if (gridOuter) {
      gridOuter.classList.add("is-loading");
    }

    if (this.gridContainer) {
      this.gridContainer.style.opacity = "0.3";
      this.gridContainer.style.pointerEvents = "none";
    }
  }

  hideLoading() {
    const gridOuter = document.querySelector(".grid-outer");
    if (gridOuter) {
      gridOuter.classList.remove("is-loading");
    }

    if (this.gridContainer) {
      this.gridContainer.style.opacity = "1";
      this.gridContainer.style.pointerEvents = "auto";
    }
  }

  async loadAndSort() {
    if (this.isLoading) return;
    this.isLoading = true;

    try {
      // Step 1: Fetch ALL product metadata (lightweight JSON)
      const initialUrl = this.buildJsonUrl(1);
      const initialRes = await fetch(initialUrl);
      if (!initialRes.ok) throw new Error("Failed to fetch metadata");

      const initialData = await initialRes.json();
      let allProducts = [...initialData.products];
      const totalPages = initialData.pages || 1;

      // Fetch remaining pages in parallel
      if (totalPages > 1) {
        const promises = [];
        for (let p = 2; p <= totalPages; p++) {
          promises.push(fetch(this.buildJsonUrl(p)).then((r) => r.json()));
        }
        const results = await Promise.all(promises);
        results.forEach((data) => {
          if (data.products) {
            allProducts = allProducts.concat(data.products);
          }
        });
      }

      this.allProductsMetadata = allProducts;
      console.info(
        "[custom-sort] Loaded metadata for",
        allProducts.length,
        "products"
      );

      // Step 2: Sort in memory
      this.sortMetadata();

      // Step 3: Render current page
      await this.renderCurrentPage();
    } catch (error) {
      console.error("[custom-sort] Error:", error);
      this.hideLoading();
    } finally {
      this.isLoading = false;
    }
  }

  sortMetadata() {
    // Deduplicate by ID
    const unique = new Map();
    this.allProductsMetadata.forEach((p) => unique.set(p.id, p));
    const products = Array.from(unique.values());

    const newProducts = products.filter((p) => p.is_new);
    const regularProducts = products.filter((p) => !p.is_new);

    // Sort alphabetically
    const sortFn = (a, b) => a.title.localeCompare(b.title, "pt");
    newProducts.sort(sortFn);
    regularProducts.sort(sortFn);

    this.sortedHandles = [...newProducts, ...regularProducts].map(
      (p) => p.handle
    );

    console.info(
      "[custom-sort] Sorted. New:",
      newProducts.length,
      "Regular:",
      regularProducts.length
    );
  }

  async renderCurrentPage() {
    const startIndex = (this.currentPage - 1) * this.productsPerPage;
    const endIndex = startIndex + this.productsPerPage;
    const pageHandles = this.sortedHandles.slice(startIndex, endIndex);

    if (pageHandles.length === 0) {
      this.gridContainer.innerHTML =
        '<div class="no-results"><p><strong>Nenhum produto encontrado</strong></p></div>';
      this.hideLoading();
      return;
    }

    // Fetch product cards HTML in parallel
    const htmlMap = new Map();
    const fetchQueue = [...pageHandles];
    const CONCURRENCY = 5;

    const fetchWorker = async () => {
      while (fetchQueue.length > 0) {
        const handle = fetchQueue.shift();
        try {
          const res = await fetch(`/products/${handle}?view=card`);
          const text = await res.text();
          htmlMap.set(handle, text);
        } catch (e) {
          console.error("Failed to fetch", handle, e);
        }
      }
    };

    await Promise.all(Array(CONCURRENCY).fill(0).map(fetchWorker));

    // Construct HTML
    let html = "";

    pageHandles.forEach((h) => {
      if (htmlMap.has(h)) {
        html += htmlMap.get(h);
      }
    });

    // Re-insert featured blocks at the end (simplest strategy)
    // If we wanted to intersperse, we'd need original indices logic,
    // but usually appending for page 1 is acceptable for custom sorts.
    if (this.currentPage === 1 && this.featuredBlocks.length > 0) {
      this.featuredBlocks.forEach((b) => (html += b.html));
    }

    this.gridContainer.innerHTML = html;
    this.renderPagination();
    this.hideLoading();

    // Trigger Shopify's section rendering events
    document.dispatchEvent(new CustomEvent("shopify:section:load"));
  }

  buildJsonUrl(page) {
    const url = new URL(this.basePath, window.location.origin);
    const params = new URLSearchParams(this.baseSearchParams);
    params.set("view", "json-products");
    params.set("page", page);
    params.set("sort_by", "title-ascending");
    url.search = params.toString();
    return url.toString();
  }

  renderPagination() {
    const totalPages = Math.ceil(
      this.sortedHandles.length / this.productsPerPage
    );
    if (totalPages <= 1) {
      const p = document.querySelector(".pagination");
      if (p) p.remove();
      return;
    }

    let html =
      '<nav class="pagination" role="navigation" aria-label="Pagination"><div class="pagination__inner">';

    if (this.currentPage > 1) {
      html += `<a href="#" class="pagination__item pagination__item--prev" data-page="${
        this.currentPage - 1
      }" aria-label="Previous page"><svg width="20" height="20" viewBox="0 0 20 20"><path d="M12 15l-5-5 5-5" stroke="currentColor" fill="none" stroke-width="2"/></svg></a>`;
    }

    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

    if (start > 1)
      html += `<a href="#" class="pagination__item" data-page="1">1</a><span class="pagination__item">...</span>`;

    for (let i = start; i <= end; i++) {
      if (i === this.currentPage)
        html += `<span class="pagination__item pagination__item--current">${i}</span>`;
      else
        html += `<a href="#" class="pagination__item" data-page="${i}">${i}</a>`;
    }

    if (end < totalPages)
      html += `<span class="pagination__item">...</span><a href="#" class="pagination__item" data-page="${totalPages}">${totalPages}</a>`;

    if (this.currentPage < totalPages) {
      html += `<a href="#" class="pagination__item pagination__item--next" data-page="${
        this.currentPage + 1
      }" aria-label="Next page"><svg width="20" height="20" viewBox="0 0 20 20"><path d="M8 5l5 5-5 5" stroke="currentColor" fill="none" stroke-width="2"/></svg></a>`;
    }

    html += "</div></nav>";

    let container = document.querySelector(".pagination");
    if (container) container.outerHTML = html;
    else {
      // If no pagination exists, append it after the grid
      // Need to find correct container. Usually parent of grid-outer or grid-outer itself
      const gridOuter = document.querySelector(".grid-outer");
      if (gridOuter) {
        gridOuter.insertAdjacentHTML("beforeend", html);
      }
    }

    document.querySelectorAll(".pagination__item[data-page]").forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        const page = parseInt(el.dataset.page);
        const url = new URL(window.location);
        url.searchParams.set("page", page);
        window.history.pushState({}, "", url);
        this.currentPage = page;
        this.showLoading();
        this.renderCurrentPage();
        window.scrollTo({
          top: this.gridContainer.offsetTop - 100,
          behavior: "smooth",
        });
      });
    });
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new CustomCollectionSort();
});
